/**
 * Self-heals a stale tab that tries to lazy-load a route chunk which no
 * longer exists after a deploy (Vite hashes chunk filenames per build, so an
 * old `index.html`/bundle referencing a now-deleted chunk 404s on dynamic
 * `import()`).
 *
 * Real incident: prod Sentry — `TypeError: Failed to fetch dynamically
 * imported module: .../assets/PrePrintPage-CaSAKGm8.js` on a tab left open
 * across a deploy.
 *
 * Vite's client runtime dispatches a `vite:preloadError` event on `window`
 * when a dynamic import fails to load. We reload exactly once per build id
 * (guarded via `sessionStorage`, keyed by `__BUILD_ID__`) so a genuinely
 * offline tab does not loop reloading forever — after one failed attempt we
 * let the error surface normally (Sentry, error boundary, etc).
 */

function reloadGuardKey(buildId: string): string {
  return `preload-error-reloaded:${buildId}`;
}

/** Exported for tests: decides whether a preload-error reload should fire. */
export function shouldReloadOnPreloadError(storage: Pick<Storage, 'getItem' | 'setItem'>, buildId: string): boolean {
  const key = reloadGuardKey(buildId);
  try {
    if (storage.getItem(key)) return false;
    storage.setItem(key, '1');
    return true;
  } catch {
    // Storage unavailable (kiosk lockdown, private browsing, quota) — fail
    // CLOSED. Without the guard we cannot bound the retry count, and a
    // persistent chunk 404 would reload the station at page-load cadence
    // forever. Better to let the error boundary + Sentry surface it once.
    return false;
  }
}

export function initPreloadErrorReload(): void {
  if (import.meta.env.DEV) return;

  window.addEventListener('vite:preloadError', (event) => {
    if (shouldReloadOnPreloadError(window.sessionStorage, __BUILD_ID__)) {
      event.preventDefault();
      window.location.reload();
    }
    // else: already tried once for this build — let the error surface
    // (Sentry / error boundary) instead of looping.
  });
}
